// externals
import type { KeyboardEvent, FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';

// libraries
import { formatUtils, dealLockHooks } from '@makemydeal/dr-dash-components';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import { useSelector } from 'react-redux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';

// components
import { ManualAccessoryActionIcon } from './ManualAccessoryActionIcon.interstate';
import { ManualAccessoryNameInput } from './ManualAccessoryNameInput.interstate';
import { ManualAccessoryIdField } from './ManualAccessoryIdField.interstate';
import { ManualAccessoryInstallField } from './ManualAccessoryInstallField.interstate';
import { ManualAccessorySaleField } from './ManualAccessorySaleField.interstate';
import { ManualAccessoryCostField } from './ManualAccessoryCostField.interstate';
import { ManualAccessoryResidualField } from './ManualAccessoryResidualField.interstate';
import { ManualAccessoryProfitField } from './ManualAccessoryProfitField.interstate';
import { ManualAccessoryUpfrontField } from './ManualAccessoryUpfrontField.interstate';
import { ManualAccessoryWeOweField } from './ManualAccessoryWeOweField.interstate';

// interfaces/types
import type { AccessoryFormPropsInterstate, ManualAccessoryPicklistItem } from '../manualAccessoriesTypes';
import type { Accessory } from '@makemydeal/dr-dash-types';

// utils
import { buildColumnMappings } from '../manualAccessoriesUtils';

// styles
import {
    ManualAccessoriesFormRowContainer,
    AccessoryUpfrontWeOweBaseContainer,
    AccessoryBaseFieldsContainer
} from '../../ManualAccessories.interstate.styles';
import { Box } from '@interstate/components/Box';
import { convertToString } from '../../../utils/convertToString';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

export const AccessoryRowForm: FC<AccessoryFormPropsInterstate> = ({
    accessory,
    accessories,
    index,
    updateAccessory,
    updateAccessoryRow,
    removeAccessory,
    restoreAccessory,
    isLeaseLayout,
    handleSoftSave,
    handleRecalcModal,
    getFieldValidation,
    validateField,
    shouldTriggerValidation = false,
    baseBreakPoint
}) => {
    const { amount, deleted, name, code, cost, profit, residual, upFront, weOwe, installPrice } = accessory;

    const accessoryRef = useRef(accessory);
    accessoryRef.current = accessory;

    const optionalClassName = isLeaseLayout ? 'lease-layout' : '';

    const columnMappings = buildColumnMappings(accessory);
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealXgVersion = useSelector(offerReduxSelectors.getDealXgVersion);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const vin = useSelector(vehicleSelectors.getVehicleVin);
    const isLocked = dealLockHooks.useIsDealLocked();
    const prevAccessoryValue = useRef<Accessory>(cloneDeep(accessory));
    const [field, setField] = useState<string>('');
    const disabled = isLocked || deleted;

    const manualAccessoriesPickList = useSelector(dealerSelectors.getManualAccessories) as unknown as ManualAccessoryPicklistItem[];

    const isOptionalDeskingMode = useSelector(dealModeSelectors.isOptionalDeskingMode);

    const validateAndSave = useCallback(
        (fieldName: string) => {
            validateField(index, fieldName);
            if (prevAccessoryValue.current[fieldName] !== accessory[fieldName]) {
                handleSoftSave(fieldName);
                prevAccessoryValue.current[fieldName] = accessory[fieldName];
            }
        },
        [accessory, handleSoftSave]
    );

    useEffect(() => {
        if (accessories && field) {
            validateAndSave(field);
        }
    }, [accessories, field, validateAndSave]);

    const handleStringChange = (event: InterstateOnChangeEvent<string>, field: string) => {
        const value = event.target.value;

        if (accessory[field] === value) return;

        updateAccessory(index, field, value);
    };

    const handleSelectInputChange = (event: InterstateOnChangeEvent<SelectInputEventValue>, field: string) => {
        const value = event.target.value;

        const hasChanged = prevAccessoryValue.current[field] !== value;
        handleRecalcModal(field, hasChanged);

        updateAccessory(index, field, value);

        setField(field);
    };

    const handleNumberChange = (event: InterstateOnChangeEvent<string>, field: string) => {
        const value = event.target.value;

        const isOptionalField = field === columnMappings.Cost.fieldName || field === columnMappings.Sale.fieldName;
        if (!isOptionalDeskingMode || !isOptionalField) {
            const hasChanged = prevAccessoryValue.current[field] !== formatUtils.convertToNumberOrNull(value);
            handleRecalcModal(field, hasChanged);
        }

        updateAccessory(index, field, formatUtils.convertToNumberOrNull(value));
    };

    const handleInputChange = (event: InterstateOnChangeEvent<string | SelectInputEventValue>, field: string) => {
        switch (field) {
            case columnMappings.Name.fieldName:
            case columnMappings.ID.fieldName:
                handleStringChange(event as InterstateOnChangeEvent<string>, field);
                break;
            case columnMappings.Profit.fieldName:
                handleSelectInputChange(event, field);
                break;
            case columnMappings.Cost.fieldName:
            case columnMappings.Sale.fieldName:
            case columnMappings.Residual.fieldName:
            case columnMappings.Install.fieldName:
                handleNumberChange(event as InterstateOnChangeEvent<string>, field);
                break;
        }
    };

    const handleCheckboxCheck = (event: InterstateOnChangeEvent<CheckBoxEventValue>, field: string) => {
        const newValue = !!event.target.value.checked;
        updateAccessory(index, field, newValue);

        if (accessories) {
            const updatedAccessories = [...accessories];
            updatedAccessories[index] = {
                ...updatedAccessories[index],
                [field]: newValue
            };

            handleSoftSave(field, updatedAccessories);
        } else {
            handleSoftSave(field);
        }
    };

    const handleOnBlur = (fieldName: string) => {
        validateAndSave(fieldName);
    };

    const handleEnterKeyPress = (
        event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>,
        fieldName: string
    ) => {
        if (event.key === 'Enter') {
            validateAndSave(fieldName);
        }
    };

    const handleAccessorySelectedFromPickList = useCallback(
        (accessoryName: string) => {
            const selectedAccessory = manualAccessoriesPickList?.find((a: ManualAccessoryPicklistItem) => a.name === accessoryName);

            if (selectedAccessory) {
                const acc = selectedAccessory;

                const mapProfitApplication = (profitApp: string | undefined) => {
                    if (profitApp === 'Back') return 'backEndProfit';
                    if (profitApp === 'Front') return 'frontEndProfit';
                    return 'frontEndProfit';
                };

                const updatedAccessory: Accessory = {
                    name: acc.name || '',
                    code: acc.code || '',
                    cost: acc.cost,
                    amount: acc.price,
                    originalAmount: acc.price,
                    installPrice: acc.install,
                    profit: mapProfitApplication(acc.profitApplication),
                    weOwe: acc.weOwe || false,
                    upFront: acc.upfront || false,
                    residual: acc.residual || 0,
                    deleted: false
                };

                updateAccessoryRow(index, updatedAccessory);
                prevAccessoryValue.current = { ...prevAccessoryValue.current, ...updatedAccessory };

                logNewRelicPageAction('MV:Accessories - Pick List Item Selected', {
                    offerType,
                    dealXgId,
                    dealXgVersion,
                    dealerId,
                    vin,
                    accessoryName: acc.name,
                    accessoryCode: acc.code,
                    price: acc.price,
                    cost: acc.cost,
                    profitApplication: acc.profitApplication
                });
            }
        },
        [manualAccessoriesPickList, index, updateAccessoryRow, offerType, dealXgId, dealXgVersion, dealerId, vin]
    );

    return (
        <ManualAccessoriesFormRowContainer id={`accessory-row-form-${index}`} data-testid={`accessory-row-form-${index}`}>
            <AccessoryBaseFieldsContainer
                className={optionalClassName + ' compact-ui'}
                $baseBreakPoint={baseBreakPoint}
                isDealCentralMultiScenario={isDealCentralMultiScenario}
            >
                <ManualAccessoryNameInput
                    disabled={disabled}
                    index={index}
                    name={name}
                    shouldTriggerValidation={shouldTriggerValidation}
                    accessories={accessories}
                    nameColumnMapping={columnMappings.Name}
                    handleInputChange={handleInputChange}
                    handleOnBlur={handleOnBlur}
                    handleKeyPress={handleEnterKeyPress}
                    onAccessorySelectedFromPickList={handleAccessorySelectedFromPickList}
                    isOptionalDeskingMode={isOptionalDeskingMode}
                    hasError={getFieldValidation(index, columnMappings.Name.fieldName).hasError}
                    errorMessage={getFieldValidation(index, columnMappings.Name.fieldName).errorMessage}
                />
                <ManualAccessoryIdField
                    disabled={disabled}
                    index={index}
                    code={code || ''}
                    idColumnMapping={columnMappings.ID}
                    handleInputChange={handleInputChange}
                    handleOnBlur={handleOnBlur}
                    handleKeyPress={handleEnterKeyPress}
                    hasError={getFieldValidation(index, columnMappings.ID.fieldName).hasError}
                    errorMessage={getFieldValidation(index, columnMappings.ID.fieldName).errorMessage}
                />
                <ManualAccessorySaleField
                    disabled={disabled}
                    index={index}
                    amount={convertToString(amount, { skipZero: false })}
                    saleColumnMapping={columnMappings.Sale}
                    handleInputChange={handleInputChange}
                    weOwe={weOwe}
                    handleOnBlur={handleOnBlur}
                    handleKeyPress={handleEnterKeyPress}
                    accessories={accessories}
                    shouldTriggerValidation={shouldTriggerValidation}
                    isOptionalDeskingMode={isOptionalDeskingMode}
                    hasError={getFieldValidation(index, columnMappings.Sale.fieldName).hasError}
                    errorMessage={getFieldValidation(index, columnMappings.Sale.fieldName).errorMessage}
                />
                <ManualAccessoryCostField
                    disabled={disabled}
                    index={index}
                    cost={convertToString(cost, { skipZero: false })}
                    costColumnMapping={columnMappings.Cost}
                    handleInputChange={handleInputChange}
                    weOwe={weOwe}
                    handleOnBlur={handleOnBlur}
                    handleKeyPress={handleEnterKeyPress}
                    accessories={accessories}
                    hasError={getFieldValidation(index, columnMappings.Cost.fieldName).hasError}
                    errorMessage={getFieldValidation(index, columnMappings.Cost.fieldName).errorMessage}
                    shouldTriggerValidation={shouldTriggerValidation}
                    isOptionalDeskingMode={isOptionalDeskingMode}
                />
                <ManualAccessoryInstallField
                    disabled={disabled}
                    index={index}
                    install={convertToString(installPrice, { skipZero: false })}
                    installColumnMapping={columnMappings.Install}
                    handleInputChange={handleInputChange}
                    handleOnBlur={handleOnBlur}
                    handleKeyPress={handleEnterKeyPress}
                    hasError={getFieldValidation(index, columnMappings.Install.fieldName).hasError}
                    errorMessage={getFieldValidation(index, columnMappings.Install.fieldName).errorMessage}
                />
                {isLeaseLayout ? (
                    <ManualAccessoryResidualField
                        disabled={disabled}
                        index={index}
                        residual={convertToString(residual, { skipZero: false })}
                        residualColumnMapping={columnMappings.Residual}
                        handleInputChange={handleInputChange}
                        handleOnBlur={handleOnBlur}
                        handleKeyPress={handleEnterKeyPress}
                        accessories={accessories}
                        hasError={getFieldValidation(index, columnMappings.Residual.fieldName).hasError}
                        errorMessage={getFieldValidation(index, columnMappings.Residual.fieldName).errorMessage}
                    />
                ) : null}
                <ManualAccessoryProfitField
                    disabled={disabled}
                    index={index}
                    profit={profit}
                    profitColumnMapping={columnMappings.Profit}
                    handleInputChange={handleInputChange}
                    baseBreakPoint={baseBreakPoint}
                />
                <AccessoryUpfrontWeOweBaseContainer
                    className={`${optionalClassName} we-owe-form-control`}
                    $baseBreakPoint={baseBreakPoint}
                    isDealCentralExperience={isDealCentralExperience}
                >
                    {isLeaseLayout ? (
                        <ManualAccessoryUpfrontField
                            disabled={disabled}
                            index={index}
                            upFront={upFront}
                            upfrontColumnMapping={columnMappings.Upfront}
                            handleCheckboxCheck={handleCheckboxCheck}
                        />
                    ) : null}
                    <Box minWidth={90}>
                        <ManualAccessoryWeOweField
                            disabled={disabled}
                            index={index}
                            weOwe={weOwe}
                            weOweColumnMapping={columnMappings.WeOwe}
                            handleCheckboxCheck={handleCheckboxCheck}
                        />
                    </Box>
                    <ManualAccessoryActionIcon
                        deleted={deleted}
                        index={index}
                        restoreAccessory={restoreAccessory}
                        removeAccessory={removeAccessory}
                        disabled={isLocked}
                    />
                </AccessoryUpfrontWeOweBaseContainer>
            </AccessoryBaseFieldsContainer>
        </ManualAccessoriesFormRowContainer>
    );
};
